import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import MainLayout from "@/components/layout/MainLayout";
import PageHeader from "@/components/layout/PageHeader";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  MessageCircle,
  CreditCard,
  Settings,
  Zap,
  Link,
  Phone,
} from "lucide-react";

const SupportPage = () => {
  const { userProfile } = useAuth();
  const firstName = userProfile?.full_name?.split(" ")[0] || "there";

  const faqCategories = [
    {
      title: "Billing & Payments",
      icon: <CreditCard className="h-5 w-5" />,
      questions: [
        {
          question: "Do you offer a free trial or a money-back guarantee?",
          answer:
            "Yes! We offer a 3-day free trial so you can explore all our features before committing. Additionally, we provide a 2-week full money-back guarantee, no questions asked. You can start using Hubflo today, and if it's not the right fit, we'll refund you in full.",
        },
        {
          question: "How does billing work?",
          answer:
            "Once you subscribe, you'll be charged immediately, and then automatically on the same date each month. This ensures uninterrupted access to your subscription benefits.",
        },
        {
          question: "How do I cancel my subscription and get a refund?",
          answer:
            "You can manage or cancel your subscription at any time through the billing section, which will direct you to your Stripe subscription management dashboard. If you cancel, you'll continue to have access until the end of your current billing period. If you cancel within the first 14 days, you're eligible for a full refund. Just reach out to us via chat, and we'll process the cancellation and refund within 24 to 48 hours. We always appreciate any feedback you can provide!",
        },
        {
          question: "Do voice minutes roll over each month?",
          answer:
            "Voice minutes are set on a monthly basis and do not roll over. For example, if your plan includes 450 voice minutes per month, that's your monthly allocation for phone agent calls. The minute count resets every month. If you exceed your limit, overage charges apply: $0.30/min for Pro plans and $0.21/min for Business plans.",
        },
      ],
    },
    {
      title: "Getting Started",
      icon: <Settings className="h-5 w-5" />,
      questions: [
        {
          question: "How to Get Started with Hubflo",
          answer: `Hubflo offers three main AI solutions to help your business:

## AI Assistants
Ready-to-use AI tools that generate beautifully formatted outputs in seconds. Simply provide a few inputs and get professional results like email sequences, marketing copy, or business plans. Unlike ChatGPT where you need to craft prompts repeatedly, our assistants are pre-trained for specific tasks and deliver consistent, structured outputs every time.

## Chat Agents  
1. **Train on your website**: Click "Train agent for my website" - takes about one minute
2. **Create your agent**: Go to Agents section and click "New Agent"
3. **Pick a template**: Choose from Lexi (lead capture), Cori (customer support), or industry-specific templates
4. **Customize**: Fine-tune instructions, tone, add knowledge, and enable skills like call booking

## Voice Agents
Set up AI phone agents for your business. You'll need:
- A Twilio phone number
- Custom voice training on your business
- Integration with your existing systems

Each solution can be customized and integrated to work seamlessly with your business processes.`,
        },
      ],
    },
    {
      title: "Customization & Control",
      icon: <Settings className="h-5 w-5" />,
      questions: [
        {
          question: "How customizable are the agents?",
          answer:
            "You can fully customize your agents in numerous ways. We offer more than 30 different settings, allowing you to tailor the agent's instructions, add custom behaviors, and connect it to your website or external resources like Google. You can also customize the UI by adding your banner, logo, custom colors, and avatars. You can fine-tune the agent's personality, response length, tone, and creativity level. This makes Hubflo AI Agents extremely powerful and still very easy to get started with.",
        },
        {
          question: "How can I control what my agent says to my clients?",
          answer:
            "You have a wide range of options to customize your agents. You can fine-tune instructions, add restrictions to prevent certain topics, and implement custom behaviors—like defining specific responses or actions based on client input. You can also adjust the agent's personality, tone of voice, response length, and more. Plus, you have full visibility into all conversation history, so you can review interactions and refine your agent's behavior until it aligns with your preferences.",
        },
      ],
    },
    {
      title: "Phone Agents",
      icon: <Phone className="h-5 w-5" />,
      questions: [
        {
          question: "How do I connect a phone number to my voice agent?",
          answer:
            "To connect a phone number to your voice agent, you'll need a Twilio account. Once you have Twilio set up, you can either get a new phone number through Twilio or keep using your existing business number by simply routing calls from your current number to your new Twilio number. This way, customers can still call your familiar business number, and the calls will automatically be handled by your AI voice agent.",
        },
        {
          question: "How do I configure my voice agent?",
          answer:
            "Configuring your voice agent is straightforward and customizable. You can select from various AI voices to match your brand personality, create a custom greeting message that welcomes callers, write detailed instructions that guide how your agent should handle different types of calls, and even upload documents to train your agent with specific knowledge about your business, products, or services. This ensures your voice agent can provide accurate and helpful responses to your callers.",
        },
      ],
    },
    {
      title: "Features & Capabilities",
      icon: <Zap className="h-5 w-5" />,
      questions: [
        {
          question:
            "What makes Hubflo AI Agents unique compared to ChatGPT or other AI solutions?",
          answer:
            "Hubflo offers specialized AI solutions for different business needs. Our AI Assistants are pre-trained tools that generate professional, formatted outputs from simple inputs - perfect for creating marketing materials, business plans, or email sequences without needing to craft prompts. Voice Agents handle phone interactions. Unlike ChatGPT's general conversational approach, our solutions are purpose-built for specific business functions with consistent, professional results.",
        },
        {
          question:
            "How do I get notified when an agent interacts with a lead or a client?",
          answer:
            "At the end of every conversation, you'll receive a comprehensive summary delivered to the email of your choice. Conversations automatically close after 10 minutes of inactivity. The summary includes all collected information, a conversation recap, any tags, and the priority level of the interaction. The AI also flags if any action is required on your end, which you can manage directly from your dashboard. Additionally, you can use our webhook feature to get notifications on platforms like Slack or to send data to your CRM using tools like Zapier, Make, or n8n. It's super easy!",
        },
        {
          question: "What AI model powers Hubflo AI Agents?",
          answer:
            "Our agents are powered by a combination of the latest OpenAI and Claude models, specifically GPT 4.1, GPT 5, and Claude 4. These state-of-the-art models exhibit human-like capabilities, ensuring high-quality interactions. While they're pre-configured by default, we plan to offer customization options in the future, giving you even more control.",
        },
      ],
    },
    {
      title: "Security & Data",
      icon: <Settings className="h-5 w-5" />,
      questions: [
        {
          question: "How secure is my data with Hubflo AI Agents?",
          answer:
            "Your data security is our top priority. We don't train our models on your data, and everything is securely hosted. We never share your information with any third parties. Rest assured, your data is safe with us.",
        },
        {
          question: "Can I export my data or conversation history?",
          answer:
            "Yes! While we don't yet provide a direct option to export all your information from the dashboard (though it's planned), you can reach out to our support team via chat. If you need it, we'll provide a full export of all your chat history and conversations upon request.",
        },
        {
          question: "Can I have multiple internal users on my account?",
          answer:
            "Yes! Now you can invite other internal users to collaborate if you're on Pro plan and higher. The Starter plan allows one user, while Pro and Business plans let you invite additional team members for $19 per user per month.",
        },
      ],
    },
  ];

  return (
    <MainLayout>
      <PageHeader title="Support" />

      <div className="flex-1 overflow-auto">
        {/* Full width banner with same styling as home page */}
        <div className="relative w-full">
          <div
            className="absolute inset-0 w-screen bg-gradient-to-b from-amber-100 via-amber-50 to-white"
            style={{ left: "50%", transform: "translateX(-50%)" }}
          ></div>
          <div className="relative px-6 sm:px-12 py-12 pt-20 flex flex-col justify-center items-center overflow-hidden">
            {/* WhatsApp-style Background Pattern Overlay with gradient mask */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(https://gvaimfxjrywshlnkxrbi.supabase.co/storage/v1/object/public/image_library/chat_backgrounds/Whatsapp-Style-Background.svg)`,
                backgroundSize: "cover",
                backgroundPosition: "center center",
                backgroundRepeat: "no-repeat",
                opacity: 0.06,
                zIndex: 1,
                pointerEvents: "none",
                filter: "sepia(0.3) saturate(0.8) hue-rotate(20deg)",
                mask: "linear-gradient(to bottom, black, rgba(0,0,0,0.5), transparent)",
                WebkitMask:
                  "linear-gradient(to bottom, black, rgba(0,0,0,0.5), transparent)",
              }}
            />

            <div className="text-center relative z-10">
              <h1 className="text-2xl sm:text-[32px] font-bold mb-4 sm:mb-8 tracking-tight leading-tight">
                How can we help{" "}
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-500 to-amber-500">
                  {firstName}
                </span>
                ?
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Browse our knowledge base articles below or reach out to us via
                the chat to get help.
              </p>
            </div>
          </div>
        </div>

        <div className="container mx-auto py-6 max-w-4xl">
          {/* FAQ Categories */}
          <div className="space-y-8">
            {faqCategories.map((category, categoryIndex) => (
              <div
                key={categoryIndex}
                className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6"
              >
                <div className="flex items-center gap-3 mb-6">
                  {category.icon}
                  <h2 className="text-xl font-semibold text-gray-900">
                    {category.title}
                  </h2>
                </div>

                <Accordion
                  type="single"
                  collapsible
                  className="w-full space-y-3"
                >
                  {category.questions.map((faq, questionIndex) => (
                    <AccordionItem
                      key={questionIndex}
                      value={`category-${categoryIndex}-item-${questionIndex}`}
                      className="bg-gray-50 rounded-2xl border border-gray-200 px-4"
                    >
                      <AccordionTrigger className="text-left hover:no-underline py-4 text-base font-medium text-gray-900 hover:text-[#8aa3d9]">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="pb-4 text-gray-700 leading-relaxed whitespace-pre-line">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default SupportPage;
